import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { 
  X, 
  Play, 
  Gamepad2, 
  Footprints, 
  Crosshair, 
  Users, 
  Layers, 
  User, 
  Trophy,
  Maximize2,
  Minimize2
} from "lucide-react";
import { apiRequest } from "../utils/api";
import banner1 from "../assets/banner1.webp";
import banner2 from "../assets/banner2.webp";
import banner3 from "../assets/banner3.webp";
import trapAndGooImg from "../assets/trap-and-goo.webp";
import forestRushImg from "../assets/forest-rush.webp";
import draugutsImg from "../assets/drauguts.jpg";
import frontlineImg from "../assets/frontline.webp";
import cyrusBeanJumpImg from "../assets/cyrus-bean-jump.webp";
import dandoRushImg from "../assets/dando-rush.webp";
import draughtsImg from "../assets/draughts.webp";
import gunWarsImg from "../assets/gun-wars.webp";
import omiImg from "../assets/omi.webp";
import poolLeagueImg from "../assets/pool-league.webp";
import carromLeagueImg from "../assets/carrom-league.webp";
import logo192 from "../assets/logo192.png";

// Map API thumbnailUrl filenames to local imported assets
const THUMBNAIL_MAP = {
  "trap-and-goo.jpg": trapAndGooImg,
  "trap-and-goo.webp": trapAndGooImg,
  "forest-rush.jpg": forestRushImg,
  "forest-rush.webp": forestRushImg,
  "drauguts.jpg": draugutsImg,
  "drauguts.webp": draugutsImg,
  "draughts.jpg": draughtsImg,
  "draughts.webp": draughtsImg,
  "frontline.jpg": frontlineImg,
  "frontline.webp": frontlineImg,
  "cyrus-bean-jump.webp": cyrusBeanJumpImg,
  "dando-rush.webp": dandoRushImg,
  "gun-wars.webp": gunWarsImg,
  "omi.webp": omiImg,
  "pool-league.webp": poolLeagueImg,
  "carrom-league.webp": carromLeagueImg,
  "Trap and Goo": trapAndGooImg,
  "Forest Rush": forestRushImg,
  "Draughts": draughtsImg,
  "Frontline": frontlineImg,
  "Cyrus Bean Jump": cyrusBeanJumpImg,
  "Dando Rush": dandoRushImg,
  "Gun Wars": gunWarsImg,
  "Omi": omiImg,
  "Pool League": poolLeagueImg,
  "Carrom League": carromLeagueImg,
};

// Helper to determine category icon and display label based on game title/genre
const getCategoryDetails = (game) => {
  const title = (game.title || "").toLowerCase();
  const genre = (game.category || game.genre || "").toLowerCase();

  if (title.includes("dando") || genre.includes("racing") || title.includes("car")) {
    return { icon: Gamepad2, label: "Racing" };
  }
  if (title.includes("forest") || title.includes("ninja") || title.includes("jump") || genre.includes("running")) {
    return { icon: Footprints, label: "Running" };
  }
  if (title.includes("gun") || title.includes("shoot") || title.includes("war") || title.includes("frontline")) {
    return { icon: Crosshair, label: "Shooting" };
  }
  if (title.includes("brawl") || title.includes("planet") || genre.includes("multiplayer")) {
    return { icon: Users, label: "Multiplayer" };
  }
  if (title.includes("omi") || title.includes("card")) {
    return { icon: Layers, label: "Card Games" };
  }
  if (title.includes("farm") || title.includes("trap") || title.includes("single")) {
    return { icon: User, label: "Single Player" };
  }
  if (title.includes("draughts") || title.includes("pool") || title.includes("carrom") || genre.includes("strategy")) {
    return { icon: Trophy, label: "Strategy" };
  }
  return { icon: Gamepad2, label: game.category || "Action" };
};

const Home = () => {
  const [activeGame, setActiveGame] = useState(null);
  const [games, setGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(true);
  const [gamesError, setGamesError] = useState(null);
  const navigate = useNavigate();

  const banners = [banner2, banner1, banner3];
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);

  // Fetch games from API: library games (which provides the full catalog) or fallback to featured
  useEffect(() => {
    const fetchGames = async () => {
      try {
        setGamesLoading(true);
        setGamesError(null);
        
        let loadedGames = [];
        // First try to load library games (has all 9 games for 3x3 grid)
        const libraryRes = await apiRequest("/Games/library");
        if (libraryRes.ok) {
          const data = await libraryRes.json();
          if (Array.isArray(data) && data.length > 0) {
            loadedGames = data;
          }
        }

        // If library was empty, fallback to featured games
        if (loadedGames.length === 0) {
          const featuredRes = await apiRequest("/Games/featured");
          if (featuredRes.ok) {
            const data = await featuredRes.json();
            if (Array.isArray(data)) {
              loadedGames = data;
            }
          }
        }

        const mapped = loadedGames.map((game) => ({
          id: game.id,
          title: game.title,
          category: game.genre,
          buildPath: game.launchUrl || "",
          image: (game.thumbnailUrl && THUMBNAIL_MAP[game.thumbnailUrl]) || THUMBNAIL_MAP[game.title] || null,
        }));
        
        setGames(mapped);
      } catch (err) {
        setGamesError("Could not load games. Please try again later.");
      } finally {
        setGamesLoading(false);
      }
    };

    fetchGames();
  }, []);

  // Banner auto-rotate
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBannerIndex((prevIndex) => (prevIndex + 1) % banners.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [banners.length]);

  const handleGameClick = (game) => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      navigate("/login");
    } else if (!game.buildPath) {
      return;
    } else {
      setActiveGame(game);
    }
  };

  const [isFullscreen, setIsFullscreen] = useState(false);
  const gameContainerRef = useRef(null);

  // Manage body scroll lock when gameplay is active
  useEffect(() => {
    if (activeGame) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeGame]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      const elem = gameContainerRef.current || document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch(() => {});
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    }
  };

  const handleCloseGame = () => {
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    }
    setActiveGame(null);
  };

  // --- FULL SCREEN GAMEPLAY VIEW (Rendered to body via createPortal) ---
  if (activeGame) {
    return createPortal(
      <div 
        ref={gameContainerRef}
        className="fixed inset-0 w-screen h-screen bg-black text-white flex flex-col z-[99999] overflow-hidden"
        style={{ top: 0, left: 0, width: "100vw", height: "100vh", margin: 0, padding: 0 }}
      >
        {/* Controls Overlay: Fullscreen Toggle & Close Button */}
        <div className="absolute top-4 right-4 z-[100000] flex items-center gap-3">
          <button
            onClick={toggleFullscreen}
            className="flex items-center gap-2 px-4 py-2 bg-black/85 hover:bg-black border border-white/20 hover:border-[#FF7A00] text-white font-heading font-black uppercase tracking-wider text-xs rounded-xl shadow-lg transition-all duration-300 hover:shadow-[0_0_15px_rgba(255,122,0,0.5)] cursor-pointer group"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? (
              <>
                <Minimize2 size={16} className="text-gray-400 group-hover:text-[#FF7A00] transition-colors" />
                <span className="hidden sm:inline">Exit Fullscreen</span>
              </>
            ) : (
              <>
                <Maximize2 size={16} className="text-gray-400 group-hover:text-[#FF7A00] transition-colors" />
                <span className="hidden sm:inline">Fullscreen</span>
              </>
            )}
          </button>

          <button
            onClick={handleCloseGame}
            className="flex items-center gap-2 px-4 py-2 bg-black/85 hover:bg-black border border-white/20 hover:border-[#FF7A00] text-white font-heading font-black uppercase tracking-wider text-xs rounded-xl shadow-lg transition-all duration-300 hover:shadow-[0_0_15px_rgba(255,122,0,0.5)] cursor-pointer group"
          >
            <X size={16} className="text-gray-400 group-hover:text-[#FF7A00] transition-colors" />
            <span>Close Game</span>
          </button>
        </div>

        {/* Central Full Screen WebGL Game Canvas */}
        <div className="w-full h-full relative flex items-center justify-center bg-black">
          <iframe
            src={
              activeGame.buildPath.includes("?")
                ? `${activeGame.buildPath}&token=${encodeURIComponent(localStorage.getItem("accessToken") || "")}`
                : `${activeGame.buildPath}?token=${encodeURIComponent(localStorage.getItem("accessToken") || "")}`
            }
            className="w-full h-full border-none shadow-2xl"
            title={activeGame.title}
            allow="autoplay; fullscreen; keyboard; gamepad"
          />
        </div>
      </div>,
      document.body
    );
  }

  // --- MAIN HOME VIEW (Matches Screenshot Layout) ---
  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-12">
      
      {/* 1. HERO BANNER - Exact layout & styling from the screenshot */}
      <section className="relative w-full h-[280px] sm:h-[340px] md:h-[380px] lg:h-[420px] rounded-3xl overflow-hidden border border-[#2B2B38] shadow-[0_15px_35px_rgba(0,0,0,0.6)] group">
        
        {/* Background Banner Carousel Images */}
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          {banners.map((banner, index) => (
            <img
              key={index}
              src={banner}
              alt={`Featured Banner ${index + 1}`}
              className={`absolute inset-0 w-full h-full object-cover object-center transition-all duration-1000 ease-in-out transform ${
                index === currentBannerIndex 
                  ? "opacity-100 scale-100" 
                  : "opacity-0 scale-105 pointer-events-none"
              }`}
            />
          ))}
        </div>

        {/* Cinematic Vignette & Dramatic Dark Gradients (ensures text readability) */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0E]/95 via-[#0B0B0E]/60 to-transparent z-10" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0E]/80 via-transparent to-black/30 z-10" />

        {/* Top-Right KARNOGAMES Badge Watermark */}
        <div className="absolute top-5 right-5 sm:top-7 sm:right-8 z-20">
          <div className="bg-black/60 backdrop-blur-md border border-white/20 px-3.5 py-1.5 rounded-lg shadow-lg flex items-center gap-2">
            <img src={logo192} alt="Logo" className="w-5 h-5 object-contain" />
            <span className="font-heading font-black text-xs tracking-[0.2em] text-white uppercase">
              KARNO<span className="text-[#FF7A00]">GAMES</span>
            </span>
          </div>
        </div>

        {/* Hero Left Content: "PLAY TOGETHER. WIN TOGETHER" */}
        <div className="absolute inset-y-0 left-0 z-20 flex flex-col justify-center px-6 sm:px-10 md:px-14 lg:px-16 max-w-2xl">
          
          <h2 className="font-heading font-black italic uppercase tracking-tight text-white text-2xl sm:text-3xl md:text-4xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
            PLAY TOGETHER.
          </h2>
          
          <h1 className="font-heading font-black italic uppercase tracking-tighter text-4xl sm:text-6xl md:text-7xl lg:text-8xl mt-1 leading-none text-transparent bg-clip-text bg-gradient-to-r from-[#FF5E0E] via-[#FF8A00] to-[#FFB800] drop-shadow-[0_4px_16px_rgba(255,94,14,0.5)]">
            WIN TOGETHER
          </h1>

          <p className="text-gray-200 text-xs sm:text-sm md:text-base font-normal tracking-wide mt-3 drop-shadow-[0_1px_4px_rgba(0,0,0,0.9)] max-w-md">
            Discover your next favorite game
          </p>

          {/* Carousel Slide Dots Indicator */}
          <div className="flex gap-2 mt-6">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentBannerIndex(index)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === currentBannerIndex 
                    ? "w-8 bg-[#FF7A00] shadow-[0_0_10px_rgba(255,122,0,0.8)]" 
                    : "w-2 bg-white/30 hover:bg-white/60"
                }`}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>

        </div>

      </section>

      {/* 2. GAME CARDS GRID - 3x3 layout as shown in screenshot */}
      <section>
        {gamesLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-4 md:gap-6">
            {[...Array(9)].map((_, i) => (
              <div 
                key={i} 
                className="w-full aspect-[16/10] sm:aspect-[16/9] rounded-xl sm:rounded-2xl bg-[#181822] border border-[#252532] animate-pulse relative overflow-hidden"
              >
                <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3 right-2 sm:right-3 flex justify-between items-center">
                  <div className="h-3 sm:h-4 w-10 sm:w-20 bg-white/10 rounded-md" />
                  <div className="h-5 sm:h-8 w-14 sm:w-24 bg-white/10 rounded-full" />
                </div>
              </div>
            ))}
          </div>
        ) : gamesError ? (
          <div className="text-center py-16 text-gray-400">
            <p className="text-sm font-bold uppercase tracking-widest">{gamesError}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-4 md:gap-6">
            {games.map((game, index) => {
              const { icon: CategoryIcon, label: categoryLabel } = getCategoryDetails(game);

              return (
                <div
                  key={game.id || index}
                  onClick={() => navigate(`/game/${game.id}`)}
                  className="group relative w-full aspect-[16/10] sm:aspect-[16/9] rounded-xl sm:rounded-2xl overflow-hidden bg-[#16161F] border border-[#262634] hover:border-[#FF7A00]/80 cursor-pointer shadow-lg transition-all duration-300 hover:shadow-[0_10px_28px_rgba(255,100,0,0.3)] hover:-translate-y-1 select-none"
                >
                  {/* Game Artwork filling the entire card */}
                  {game.image ? (
                    <img 
                      src={game.image} 
                      alt={game.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#1E1E28] to-[#121217] flex flex-col items-center justify-center p-3">
                      <Gamepad2 size={32} className="text-gray-600 mb-1 group-hover:text-[#FF7A00] transition-colors" />
                      <span className="font-heading font-black uppercase text-xs tracking-wide text-white text-center">
                        {game.title}
                      </span>
                    </div>
                  )}

                  {/* Gradient Overlay for bottom text and button clarity */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/35 to-transparent pointer-events-none" />

                  {/* Bottom Bar: Category Badge (left) & Play Now Button (right) */}
                  <div className="absolute bottom-1.5 sm:bottom-3.5 left-1.5 sm:left-3.5 right-1.5 sm:right-3.5 flex items-center justify-between z-10 gap-1">
                    
                    {/* Category Label with Icon */}
                    <div className="flex items-center gap-1 sm:gap-1.5 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)] min-w-0">
                      <CategoryIcon size={12} className="text-white shrink-0 sm:w-4 sm:h-4" />
                      <span className="font-semibold text-[9px] sm:text-xs md:text-sm tracking-wide truncate max-w-[45px] sm:max-w-none">
                        {categoryLabel}
                      </span>
                    </div>

                    {/* Orange "Play Now" Button as shown in screenshot */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/game/${game.id}`);
                      }}
                      className="flex items-center gap-0.5 sm:gap-1 bg-gradient-to-r from-[#FF5E0E] to-[#FF8A00] hover:from-[#FF6E1E] hover:to-[#FF9A10] text-white px-2 sm:px-4 md:px-5 py-0.5 sm:py-1.5 md:py-2 rounded-full font-bold text-[9px] sm:text-xs md:text-sm tracking-wide shadow-md shadow-orange-500/30 group-hover:shadow-[0_0_15px_rgba(255,94,14,0.6)] transition-all hover:scale-105 active:scale-95 shrink-0 whitespace-nowrap"
                    >
                      <Play size={9} className="fill-white sm:w-3 sm:h-3" />
                      <span>Play Now</span>
                    </button>

                  </div>

                </div>
              );
            })}
          </div>
        )}
      </section>

    </div>
  );
};

export default Home;
